
/**
 * Composant principal de pagination qui gère la navigation entre les pages.
 * Il combine plusieurs sous-composants pour offrir une expérience de navigation complète.
 * 
 * @component
 * @param {Object} props - Les propriétés du composant
 * @param {number} props.currentPage - La page actuelle (commence à 0)
 * @param {number} props.totalPages - Le nombre total de pages
 * @param {function} props.onPageChange - Fonction appelée lors du changement de page
 * @param {function} props.onHover - Fonction appelée lors du survol des boutons de navigation
 * @param {number} [props.itemsPerPage=9] - Nombre d'éléments affichés par page
 * @param {function} [props.onItemsPerPageChange] - Fonction pour modifier le nombre d'éléments par page
 */
import React, { memo, useEffect, useCallback } from 'react';
import { TooltipProvider } from "@/components/ui/tooltip";
import { PaginationNavigation } from './pagination/PaginationNavigation';
import { PaginationNumbers } from './pagination/PaginationNumbers';
import { PaginationInput } from './pagination/PaginationInput';
import { PaginationItemsPerPage } from './pagination/PaginationItemsPerPage';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onHover: (direction: 'next' | 'previous') => void;
  itemsPerPage?: number;
  onItemsPerPageChange?: (value: number) => void;
}

export const Pagination = memo(({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  onHover,
  itemsPerPage = 9,
  onItemsPerPageChange
}: PaginationProps) => {
  const preloadPages = useCallback(() => {
    if (currentPage < totalPages - 1) {
      onHover('next');
    }
    if (currentPage > 0) {
      onHover('previous');
    }
  }, [currentPage, totalPages, onHover]);

  useEffect(() => {
    preloadPages();
  }, [preloadPages]);

  if (totalPages <= 1) return null;

  return (
    <TooltipProvider>
      <nav 
        role="navigation" 
        aria-label="Navigation des pages"
        className="flex flex-wrap justify-center items-center gap-4 mt-8 animate-fade-in"
      >
        <div className="sr-only">
          {`Vous êtes actuellement sur la page ${currentPage + 1} sur ${totalPages}`}
        </div>

        <PaginationNavigation
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          onHover={onHover}
        />
        
        <div 
          className="flex flex-wrap justify-center items-center gap-4"
          role="group"
          aria-label="Options de pagination"
        >
          <PaginationNumbers
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />

          <div 
            className="flex items-center gap-2"
            role="group"
            aria-label="Contrôles de pagination"
          >
            <PaginationInput
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />

            {onItemsPerPageChange && (
              <PaginationItemsPerPage
                itemsPerPage={itemsPerPage}
                onItemsPerPageChange={onItemsPerPageChange}
              />
            )}
          </div>
        </div>

        <PaginationNavigation
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          onHover={onHover}
        />

        <div 
          className="w-full sm:w-auto text-center sm:ml-4 text-sm text-gray-600 mt-2 sm:mt-0"
          aria-live="polite"
          role="status"
        >
          Page {currentPage + 1} sur {totalPages}
        </div>
      </nav>
    </TooltipProvider>
  );
});

Pagination.displayName = 'Pagination';
